// @ts-check
/* global module require */
'use_strict';

import { ToggleTabLink } from '../tv_utils/toggle_tab_link';
import { initialize_google_autocomplete } from '../tv_third_party_integrations/initialize_google_autocomplete';

/**
 * Class to manage the UI for the products page.
 *
 * @property {object} elements An object of HTML elements.
 */
export class ProductPageView {

    /**
     * Instantiate and bind the elements.
     */
    constructor() {
        const product_images = document.querySelector('.ty-product-img') as HTMLElement;
        if (product_images) {
            const first_image = (product_images.querySelector('img') as HTMLImageElement)
            // Check for product preview canvas element.
            const product_preview = product_images.querySelector('#product-preview-container') as HTMLElement;
            if (product_preview) {
                product_preview.style.visibility = "visible";
                product_images.classList.add("tv-product-image-loaded");
            } else if (first_image) {
                if (!first_image.complete) {
                    first_image.onload = () => {
                        first_image.parentElement.style.visibility = "visible";
                        product_images.classList.add("tv-product-image-loaded");
                    };
                } else {
                    first_image.parentElement.style.visibility = "visible";
                    product_images.classList.add("tv-product-image-loaded");
                }
            }
        }

        const yotpo_qna = document.getElementById('yotpo_qna');
        if (yotpo_qna) {
            const toggle_tab_links = document.getElementsByClassName('js-product-toggle-tab-link') as HTMLCollectionOf<HTMLElement>;
            const tabs = document.getElementsByClassName('js-product-tab') as HTMLCollectionOf<HTMLElement>;

            new ToggleTabLink(toggle_tab_links, tabs);

            // Remove specs tables margin-left on mobile.
            const specs_tables = document.getElementsByClassName('flex-grow__1');
            for (const specs_table of specs_tables) {
                if (window.innerWidth < 767) {
                    specs_table.classList.remove('tv-margin__left-10');
                }
            }

            // If the user is logged in, we want to put their email in for YOTPO Q&A and hide it.
            const user_id = <HTMLInputElement> document.getElementById('user_id');
            const user_name = <HTMLInputElement> document.getElementById('user_name');
            const user_email = <HTMLInputElement> document.getElementById('user_email');

            const observer = new MutationObserver(() => {
                const yotpo_name = <HTMLInputElement> document.getElementById('yotpo_input_question_username');
                const yotpo_email = <HTMLInputElement> document.getElementById('yotpo_input_question_email');
                const yotpo_user = <HTMLElement> document.querySelector(
                    '.yotpo-footer.yotpo-animation-opacity.visible'
                );
                if (user_id && parseInt(user_id.value, 10) > 0 && user_name && user_email &&
                    yotpo_name && yotpo_email && yotpo_user
                ) {
                    yotpo_name.value = user_name.value;
                    yotpo_email.value = user_email.value;
                    yotpo_user.style.overflow = 'hidden';
                    yotpo_user.style.maxHeight = '0px';
                }
                observer.disconnect();
            });
            observer.observe(yotpo_qna, { childList: true });
            // Remove undefined cells in ty-tables
            const responsive_headers = document.getElementsByClassName('ty-table__responsive-header') as HTMLCollectionOf<HTMLElement>;
            for (let i = 0; i < responsive_headers.length; i++) {
                if (responsive_headers[i].value == undefined) {
                    responsive_headers[i].classList.add('tv-display__none');
                }
            }
        }

        const reviews_tab = document.querySelector('.js-reviews-tab');
        const yotpo_scroll_panel = document.querySelector('#js-product-details-heading .ty-product-block__rating');

        if (yotpo_scroll_panel && reviews_tab) {
            yotpo_scroll_panel.addEventListener('click', () => {
                reviews_tab.click();
                reviews_tab.scrollIntoView();
            });
        }

        const more_products_block = <HTMLElement> document.getElementsByClassName('tv-more-products-block')[0];
        if (more_products_block) {
            this.adjust_more_products_block(more_products_block);
        }

        // Create a mutation observer to see if native mobile tabs have been created.
        const tabs_container  = document.getElementsByClassName('js-product-tabs-container')[0];
        let accordion_func_ran = false;

        var observer = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                if (mutation.addedNodes.length && !accordion_func_ran) {
                    accordion_func_ran = true;
                    if (document.querySelector('.ui-accordion')) {
                        handle_accordion();
                        observer.disconnect();
                    }
                }
            });
        });

        const config = {
            childList: true,
            subtree: true,
            characterData: true
        };
          
        if (tabs_container) {
            observer.observe(tabs_container, config);
        }

        // Manage the toggling of mobile tabs if they exist.
        function handle_accordion() {
            accordion_func_ran = true;
            const accordion_headers = document.getElementsByClassName('ui-accordion-header') as HTMLCollectionOf<HTMLElement>;
            const accordion_contents = document.getElementsByClassName('ui-accordion-content') as HTMLCollectionOf<HTMLElement>;

            for (let i = 0; i < accordion_headers.length; i++) {
                accordion_headers[i].addEventListener('click', function toggle_accordion_content() {
                    if (accordion_headers[i].classList.contains('ui-state-active') 
                        && accordion_contents[i].classList.contains('ui-accordion-content-active') 
                        && !accordion_contents[i].classList.contains('hidden') 
                    ) {
                        accordion_headers[i].classList.remove('ui-accordion-header-active');
                        accordion_headers[i].classList.remove('ui-state-active');
                        accordion_contents[i].classList.remove('ui-accordion-content-active');
                        accordion_contents[i].style.display = 'none';
                    } else {
                        accordion_headers[i].classList.add('ui-accordion-header-active');
                        accordion_headers[i].classList.add('ui-state-active');
                        accordion_contents[i].classList.add('ui-accordion-content-active');
                        accordion_contents[i].style.display = 'block';
                    }
                    
                });
            }
        }

        const breadcrumbs_container = document.querySelector('.ty-breadcrumbs');
        if (breadcrumbs_container) {
            window.addEventListener("load",  () => this.handle_breadcrumbs(breadcrumbs_container));
        }

        if (/iPad|iPhone/.test(navigator.userAgent)) {
            this.handle_ios_carousel();
        }

        this.handle_delivery_estimator();

    }

    /**
     * Handle the dynamic language variable link used to open the delivery estimator.
     */
    handle_delivery_estimator() {
        const estimator_observer = new MutationObserver((mutationsList, observer) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'childList') {
                    const delivery_estimator_opener = document.getElementById('js-shipping-estimator-opener-link');
                    const delivery_estimator_top_link = document.getElementById('js-shipping-estimator-opener-top-link');
                    
                    if (delivery_estimator_opener && delivery_estimator_top_link && !delivery_estimator_top_link.dataset.hasListener) {
                        delivery_estimator_top_link.addEventListener('click', () => {
                            import('../tv_shipping/ProductShippingEstimatorView').then(
                                module => new module.ProductShippingEstimatorView());
                            initialize_google_autocomplete(true);
                            delivery_estimator_opener.click();
                        });
                        delivery_estimator_top_link.dataset.hasListener = 'true';                    
                    }
                }
            }
        });

        estimator_observer.observe(document.body, { 
            childList: true, 
            subtree: true 
        });
    }

    /**
     * Adjust the "More Products" block to remove unpopulated blocks for better flex display.
     * 
     * @param {HTMLElement} container is the "More Products" block
     * @returns {void}
     */
    adjust_more_products_block(container) : void {
        const more_products_columns = container.getElementsByClassName('ty-column4');
        if (more_products_columns.length <= 4) {
            container.getElementsByClassName('grid-list')[0].classList.add(
                'tv-display__flex',
                'justify_content__center'
            );
            for (let i = 0; i < more_products_columns.length; i++) {
                if (!more_products_columns[i].hasChildNodes()) {
                    // Remove empty columns and re-evaluate that iteration.
                    more_products_columns[i].remove();
                    i--;
                }
            }
        }
    }

    /**
     * Manage breadcrumbs for overflow length
     * 
     * @returns {void}
     */
    handle_breadcrumbs(breadcrumbs_container) {
        const truncate_if_wrapped = () => {
            if (breadcrumbs_container.offsetHeight > 50) {
                const breadcrumb_links = breadcrumbs_container.querySelectorAll('.ty-breadcrumbs__a');
                const last_breadcrumb_link = breadcrumb_links[breadcrumb_links.length - 1];

                last_breadcrumb_link.innerText = last_breadcrumb_link.innerText.substr(0,9) + '...';
            }
        };

        // Some older browsers don't support ResizeObserver
        if (typeof ResizeObserver === 'undefined') {
            truncate_if_wrapped();
            return;
        }

        // Measure in a ResizeObserver callback, which runs right after layout, instead of reading offsetHeight
        // directly on load, which forces a full style recalc.
        const observer = new ResizeObserver(() => {
            observer.disconnect();
            truncate_if_wrapped();
        });
        observer.observe(breadcrumbs_container);
    }

    /**
     * Fix issues with non-cached IOS carousel swiping
     * 
     * @returns {void}
     */
    handle_ios_carousel() : void {
        const carousel_container = document.querySelector('.ty-product-img');
         
        if (carousel_container) {
            const owl_carousel_observer = new MutationObserver(
                (mutationsList, observer) => {
                    if (carousel_container.contains(document.querySelector('.owl-wrapper'))) {
                        const owl_wrapper = document.querySelector('.owl-wrapper') as HTMLElement;
                        const image_width = parseInt(owl_wrapper.style.minWidth);
                        const owl_items = owl_wrapper.querySelectorAll<HTMLElement>('.owl-item');

                        for (const owl_item of owl_items) {
                            owl_item.style.width = image_width + 'px';
                        }

                        owl_wrapper.style.width = (image_width * owl_items.length) + 'px';

                        observer.disconnect();
                    }
                }
            );

            owl_carousel_observer.observe(document, {attributes: false, childList: true, characterData: false, subtree:true});
        }
        
    }
}

/**
 * Looks for the native product block and if it exists, initializes an instance of the ProductPageView.
 *
 * @returns {void}
 */
export function init() {
    if (document.getElementsByClassName('ty-product-block').length > 0) {
        (() => new ProductPageView())();
    }
}
